import React, {
    useRef,
    useState,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
    useLayoutEffect,
    ReactElement,
    cloneElement,
} from 'react';
import { createForm, type FormApi, type SubmissionErrors } from 'final-form';
import Dropzone from 'react-dropzone';
import { Form, Field } from 'react-final-form';
import styled from 'styled-components';
import { fromPostPersistenceMediaUI } from '../../mappers/posts/toPostMediaUI';
import { canSaveOrUpdatePost, canSavePostToDraft } from '../../utils/accessService';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeEmptyValidator } from '../../utils/validators/makeEmptyValidator';
import { makeMaxLengthValidator } from '../../utils/validators/makeMaxLengthValidator';
import { makePostLinkValidator } from '../../utils/validators/makePostLinkValidator';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../utils/validators/makeUrlValidator';
import { useDragAndDrop } from '../../utils/validators/useDragAndDrop';
import { Carousel } from '../carousel/Carousel';
import { CircularProgress } from '../feedback/CircularProgress';
import { WithHint } from '../feedback/Tooltip';
import { IconAdd } from '../icon/IconAdd';
import { IconClose } from '../icon/IconClose';
import { Button } from '../input/Button';
import { FormControl } from '../input/FormControl';
import { IconButton } from '../input/IconButton';
import { Input } from '../input/Input';
import { TextArea } from '../input/TextArea';
import { Upload } from '../input/Upload';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Image } from '../media/Image';
import { MediaCover } from '../media/MediaCover';
import { VideoComponent } from '../media/VideoComponent';
import { Typography } from '../typography/Typography';
import { UserUI } from '../users/types';
import { MediaPreview } from './MediaPreview';
import { PostStatus } from './constants';
import { ReactComponent as MediaSvg } from './resources/media.svg';
import type { PostPersistenceMediaUI, PostPersistenceUI } from './types';

const validateText = composeValidators(
    makeRequiredValidator(),
    makeEmptyValidator(),
    makeMaxLengthValidator(2200, 'Описание не должно превышать 2200 символов'),
);

const validateUrl = composeValidators(makeUrlValidator(), makePostLinkValidator());

const validateMedia = (media: any[] = []) =>
    media.length === 0 ? 'Необходимо добавить хотя бы одно фото или видео' : undefined;

const FormSection = styled(Flex).attrs({
    p: 1,
    direction: 'column',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: { sm: 1.5 },
    background: 'var(--background-primary)',
})``;

const FileInput = styled.input.attrs({ type: 'file' })`
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
`;

interface MediaEditorProps {
    readOnly: boolean;
    value?: PostPersistenceMediaUI[];
    onChange: (value: PostPersistenceMediaUI[]) => void;
    onUploadFile: (
        file: File,
        onUpdateProgress?: (progress: number) => void,
    ) => Promise<PostPersistenceMediaUI | undefined>;
}

const ACCEPT_MAPPINGS = {
    'image/*': [],
    'video/*': [],
};

const MediaEditor = (props: MediaEditorProps) => {
    const { readOnly, ...other } = props;
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const t = useTranslation();

    return (
        <Upload {...other}>
            {({ isUploading, value = [], onUploadFiles }) => (
                <Box position="relative" overflow="hidden" height="100%">
                    {value.length === 0 ? (
                        <Dropzone
                            multiple={false}
                            disabled={isUploading}
                            accept={ACCEPT_MAPPINGS}
                            onDropAccepted={onUploadFiles}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <MediaCover
                                    ratio={1}
                                    borderRadius={1.5}
                                    backgroundColor="transparent"
                                    border="2px dashed var(--divider)"
                                    {...getRootProps()}
                                >
                                    <Flex
                                        as="label"
                                        position="absolute"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                    >
                                        <Box maxWidth="260px" textAlign="center" mb={2}>
                                            <MediaSvg
                                                fill="var(--background-secondary)"
                                                height="150"
                                            />
                                            <Typography
                                                variant="body"
                                                color="primary"
                                                weight="medium"
                                                mb={0.5}
                                            >
                                                {t(
                                                    'Выберите фото или видео, или перетащите его в эту область',
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="secondary">
                                                {t('Видео не больше 200 МБ и не длиннее 3 минут')}
                                            </Typography>
                                        </Box>
                                        {isUploading ? (
                                            <CircularProgress size={32} />
                                        ) : (
                                            <Button type="button">{t('Выбрать файл')}</Button>
                                        )}
                                        <WithHint
                                            mt={1}
                                            message={
                                                'Соотношение сторон любых материалов поддерживается от 9/16 до 16/9.\n\nВес файла не может превышать 200 мб и длительности 3 минут.'
                                            }
                                        >
                                            <Typography
                                                as="a"
                                                href="#"
                                                variant="caption"
                                                color="link"
                                            >
                                                {t('Подробнее про контент')}
                                            </Typography>
                                        </WithHint>
                                        <FileInput {...getInputProps()} />
                                    </Flex>
                                </MediaCover>
                            )}
                        </Dropzone>
                    ) : (
                        <Flex
                            p={1}
                            spacing={1}
                            height="100%"
                            background="var(--background-primary)"
                        >
                            <Box position="relative" width="100%">
                                <MediaEditorCarousel
                                    items={value}
                                    activeIndex={activeIndex}
                                    onSlideChange={setActiveIndex}
                                />

                                {value.length > 1 && (
                                    <Stack
                                        count={value.length}
                                        direction="row"
                                        position="absolute"
                                        p={1}
                                        left="50%"
                                        bottom={1}
                                        spacing={1}
                                        borderRadius={1.5}
                                        transform="translate(-50%, 0)"
                                        background="var(--control-secondary-background)"
                                        backdropFilter="blur(20px)"
                                        renderItem={(index) => (
                                            <Box
                                                key={index}
                                                width={6}
                                                height={6}
                                                borderRadius="50%"
                                                background="var(--control-secondary-color)"
                                                opacity={activeIndex === index ? 1 : 0.5}
                                            />
                                        )}
                                    />
                                )}
                            </Box>

                            <MediaEditorPreview
                                readOnly={readOnly}
                                items={value}
                                isUploading={isUploading}
                                activeIndex={activeIndex}
                                onSelect={setActiveIndex}
                                onChange={props.onChange}
                                onUploadFiles={onUploadFiles}
                            />
                        </Flex>
                    )}
                </Box>
            )}
        </Upload>
    );
};

interface MediaEditorCarouselProps {
    items: PostPersistenceMediaUI[];
    activeIndex: number;
    onSlideChange: (index: number) => void;
}

const MediaEditorCarousel = (props: MediaEditorCarouselProps) => {
    const { items, activeIndex, onSlideChange } = props;
    const [firstMedia] = items;
    const ratio = (firstMedia.width || 1) / (firstMedia.height || 1);

    return (
        <Carousel
            data-carousel
            count={items.length}
            width="100%"
            borderRadius={1}
            overflow="hidden"
            activeIndex={activeIndex}
            onSlideChange={onSlideChange}
            renderItem={(index) => {
                const media = items[index];

                return (
                    <Box
                        key={index}
                        height="100%"
                        position="relative"
                        background="var(--background-secondary)"
                    >
                        <Flex alignItems="center" justifyContent="center">
                            <MediaCover ratio={ratio} width="100%">
                                {media.type === 'image' && <Image src={media.url} />}
                                {media.type === 'video' && <VideoComponent src={media.url} />}
                            </MediaCover>
                        </Flex>
                    </Box>
                );
            }}
        />
    );
};

interface MediaEditorPreviewProps {
    readOnly: boolean;
    size?: number;
    items: PostPersistenceMediaUI[];
    isUploading: boolean;
    activeIndex: number;
    onSelect: (index: number) => void;
    onChange: (value: PostPersistenceMediaUI[]) => void;
    onUploadFiles: (files: File[]) => void;
}

const MediaEditorPreview = (props: MediaEditorPreviewProps) => {
    const {
        size = 80,
        readOnly,
        activeIndex,
        items,
        isUploading,
        onSelect,
        onChange,
        onUploadFiles,
    } = props;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const controllerDnD = useDragAndDrop({
        onDrop: (fromIndex, toIndex) => {
            const orderedItems = [...items];

            [orderedItems[fromIndex], orderedItems[toIndex]] = [
                orderedItems[toIndex],
                orderedItems[fromIndex],
            ];

            onChange(orderedItems);
            onSelect(toIndex);
        },
    });

    useLayoutEffect(() => {
        if (containerRef.current) {
            const element = containerRef.current.children[activeIndex] as HTMLElement | undefined;
            const left = Math.max(
                (element?.offsetLeft ?? 0) - containerRef.current.offsetWidth / 2,
                0,
            );

            if (containerRef.current?.scrollLeft !== left) {
                containerRef.current.scrollTo({ left });
            }
        }
    }, [activeIndex, containerRef.current]);

    return (
        <Flex
            ref={containerRef}
            disableScrollbar
            direction="row"
            maxWidth="100%"
            overflowY="auto"
            spacing={1}
        >
            {items.map((item, index) => {
                const mediaUI = fromPostPersistenceMediaUI(item);

                if (!mediaUI) {
                    return null;
                }

                return (
                    <Box
                        key={item.id}
                        draggable
                        minWidth={size}
                        cursor="pointer"
                        position="relative"
                        overflow="hidden"
                        borderRadius={2}
                        data-position={index}
                        border={
                            activeIndex === index
                                ? '2px solid var(--color-primary)'
                                : '2px solid transparent'
                        }
                        transform="translate3d(0,0, 0)"
                        onDrop={controllerDnD.onDrop}
                        onDragStart={controllerDnD.onDragStart}
                        onDragLeave={controllerDnD.onDragLeave}
                        onDragOver={controllerDnD.onDragOver}
                        opacity={controllerDnD.payload.draggedTo === Number(index) ? 0.125 : 1}
                        onClick={() => onSelect(index)}
                    >
                        <MediaCover ratio={1} width="100%">
                            <MediaPreview media={mediaUI} />
                        </MediaCover>
                        {!readOnly && (
                            <Box position="absolute" top={1} right={1}>
                                <IconButton
                                    size="xs"
                                    shape="rect"
                                    variant="filled"
                                    onClick={(event) => {
                                        onChange(items.filter((i) => i !== item));
                                        event.stopPropagation();
                                    }}
                                >
                                    <IconClose />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                );
            })}
            {items.length < 10 && !readOnly && (
                <Dropzone
                    multiple={false}
                    disabled={isUploading}
                    accept={ACCEPT_MAPPINGS}
                    onDrop={onUploadFiles}
                >
                    {({ getRootProps, getInputProps }) => (
                        <Flex
                            {...getRootProps()}
                            position="relative"
                            minWidth={size}
                            height={size}
                            borderRadius={2}
                            alignItems="center"
                            cursor={isUploading ? 'not-allowed' : 'pointer'}
                            justifyContent="center"
                            border="1px solid var(--divider)"
                        >
                            {isUploading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconAdd fontSize={24} color="var(--color-primary)" />
                            )}
                            <FileInput {...getInputProps()} />
                        </Flex>
                    )}
                </Dropzone>
            )}
        </Flex>
    );
};

interface PostPersistenceFormProps {
    postId?: string;
    initialValues?: Partial<PostPersistenceUI>;
    onUploadFile: (
        file: File,
        onUpdateProgress?: (progress: number) => void,
    ) => Promise<PostPersistenceMediaUI | undefined>;
    onSubmit: (values: PostPersistenceUI) => Promise<SubmissionErrors>;
    onRemove: VoidFunction;
    onSaveToDraft: VoidFunction;
    onFieldBlur: (fieldName: string, value: string) => void;
    user?: UserUI;
    productsSlot?: ReactElement;
    productsChanged: boolean;
    onSiteUrlPaste?: (url: string) => void;
}

export const PostPersistenceForm = forwardRef(
    (props: PostPersistenceFormProps, ref: ForwardedRef<FormApi<PostPersistenceUI>>) => {
        const {
            initialValues = {},
            onSubmit,
            onRemove,
            onUploadFile,
            user,
            onSaveToDraft,
            onFieldBlur,
            productsSlot,
            productsChanged,
            onSiteUrlPaste,
        } = props;
        const formRef = useRef(createForm<PostPersistenceUI>({ initialValues, onSubmit }));
        const { status } = initialValues;
        const t = useTranslation();

        useImperativeHandle(ref, () => formRef.current);

        return (
            // @ts-ignore onSubmit передается через form
            <Form
                form={formRef.current}
                onSubmit={onSubmit}
                render={({ form, invalid, submitting, pristine, submitError, handleSubmit }) => (
                    <Flex
                        as="form"
                        width="100%"
                        flexGrow={{ xs: 1, sm: 0 }}
                        spacing={{ xs: 1, md: 2 }}
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                        onSubmit={handleSubmit}
                    >
                        <FormSection width={{ md: '40%' }} minWidth={{ md: '320px' }}>
                            <Field
                                name="media"
                                validate={validateMedia}
                                render={({ input, meta }) => (
                                    <Box>
                                        <MediaEditor
                                            {...input}
                                            readOnly={
                                                status === PostStatus.PUBLISHED ||
                                                status === PostStatus.LIMITED
                                            }
                                            onUploadFile={onUploadFile}
                                        />
                                        {meta.touched && Boolean(meta.error) && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                textAlign="center"
                                                mt={1}
                                            >
                                                {meta.error}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        </FormSection>
                        <FormSection px={2} flexGrow={1}>
                            <Box>
                                <Field
                                    name="text"
                                    validate={validateText}
                                    render={({ input, meta }) => (
                                        <FormControl
                                            mb={2}
                                            label={t('Описание')}
                                            hint={t(
                                                'Текст должен быть грамотным, не содержать ссылки, нецензурные выражения и жаргонизмы.\n\nКоличество символов принимается от\xa01 до\xa02000.',
                                            )}
                                            error={meta.touched && Boolean(meta.error)}
                                            message={meta.touched ? meta.error : undefined}
                                        >
                                            <TextArea
                                                {...input}
                                                rows={2}
                                                autoComplete="off"
                                                placeholder={t('Добавьте описание поста')}
                                                onBlur={(event) => {
                                                    onFieldBlur('text', event.target.value);
                                                    input.onBlur(event);
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                                <Field
                                    name="site"
                                    validate={validateUrl}
                                    render={({ input, meta }) => (
                                        <FormControl
                                            mb={2}
                                            label={t('Ссылка для кнопки в посте')}
                                            hint={t(
                                                'Указывайте полную ссылку без сокращений и\xa0utm_source. Сервис добавит данную метку самостоятельно utm_source=rythm.\n\nМожно добавлять другие utm (medium, term и тп) и кастомные параметры',
                                            )}
                                            error={meta.touched && Boolean(meta.error)}
                                            message={meta.touched ? meta.error : undefined}
                                        >
                                            <Input
                                                {...input}
                                                onPaste={(event) => {
                                                    if (onSiteUrlPaste) {
                                                        onSiteUrlPaste(
                                                            event.clipboardData.getData(
                                                                'text/plain',
                                                            ),
                                                        );
                                                    }
                                                }}
                                                type="text"
                                                autoComplete="off"
                                                placeholder="https://"
                                                onBlur={(event) => {
                                                    onFieldBlur('site', event.target.value);
                                                    input.onBlur(event);
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                            </Box>
                            {productsSlot && cloneElement(productsSlot)}
                            <Box mt="auto" py={1}>
                                {submitError && (
                                    <Typography variant="caption" color="error" mb={1}>
                                        {submitError}
                                    </Typography>
                                )}
                                <Flex direction="row" spacing={1}>
                                    {status === PostStatus.MODERATION ||
                                    status === PostStatus.LIMITED ||
                                    status === PostStatus.PUBLISHED ? (
                                        <Button
                                            type="button"
                                            width="100%"
                                            variant="outlined"
                                            onClick={onRemove}
                                        >
                                            {t('Удалить')}
                                        </Button>
                                    ) : (
                                        <Button
                                            type="submit"
                                            width="100%"
                                            variant="outlined"
                                            disabled={
                                                (pristine && !productsChanged) ||
                                                submitting ||
                                                invalid ||
                                                !canSavePostToDraft(user?.profile?.id, user)
                                            }
                                            onClick={() => {
                                                form.change('status', PostStatus.DRAFT);
                                                onSaveToDraft();
                                            }}
                                        >
                                            {t('В черновики')}
                                        </Button>
                                    )}

                                    <Button
                                        type="submit"
                                        width="100%"
                                        disabled={
                                            (status !== PostStatus.DRAFT &&
                                                pristine &&
                                                !productsChanged) ||
                                            submitting ||
                                            invalid ||
                                            !canSaveOrUpdatePost(user?.profile?.id, user)
                                        }
                                        onClick={() => form.change('status', PostStatus.MODERATION)}
                                    >
                                        {!status || status === PostStatus.DRAFT
                                            ? t('Опубликовать')
                                            : t('Переопубликовать')}
                                    </Button>
                                </Flex>
                            </Box>
                        </FormSection>
                    </Flex>
                )}
            />
        );
    },
);
